/**
 * Google Tag Manager Consent Mode v2 Integration
 * 
 * This module handles communication with GTM via the dataLayer.
 * It sets default denied state before GTM loads and updates consent on user choice.
 * 
 * @see https://developers.google.com/tag-platform/security/guides/consent
 */

import { ConsentState } from './consentConfig';

// Extend window type for dataLayer and gtag
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

/**
 * Initialize dataLayer and gtag function if not already present
 */
function ensureDataLayer(): void {
  window.dataLayer = window.dataLayer || [];
  
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer.push(args);
    };
  }
}

/**
 * Push default denied consent state
 * This MUST be called BEFORE GTM script loads
 */
export function setDefaultConsentState(): void {
  ensureDataLayer();
  
  window.gtag('consent', 'default', {
    'analytics_storage': 'denied',
    'ad_storage': 'denied',
    'ad_user_data': 'denied',
    'ad_personalization': 'denied',
    'functionality_storage': 'granted', // For necessary cookies
    'personalization_storage': 'granted', // For user preferences
    'security_storage': 'granted', // For security features
    'wait_for_update': 500, // Wait 500ms for consent update
  });
  
  // Set region-specific defaults for EU/EEA
  window.gtag('consent', 'default', {
    'analytics_storage': 'denied',
    'ad_storage': 'denied',
    'ad_user_data': 'denied',
    'ad_personalization': 'denied',
    'region': ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 
               'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
               'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'NO', 'IS', 'LI', 'GB'],
  });
}

/**
 * Update consent state based on user choice
 */
export function updateGTMConsent(state: ConsentState): void {
  ensureDataLayer();
  
  window.gtag('consent', 'update', {
    'analytics_storage': state.analytics ? 'granted' : 'denied',
    'ad_storage': state.marketing ? 'granted' : 'denied',
    'ad_user_data': state.marketing ? 'granted' : 'denied',
    'ad_personalization': state.marketing ? 'granted' : 'denied',
  });
  
  // Push consent update event for GTM triggers
  window.dataLayer.push({
    'event': 'consent_update',
    'consent_analytics': state.analytics,
    'consent_marketing': state.marketing,
  });
  
  // If any consent was granted, trigger a virtual page view so tags can fire
  // Tags blocked on initial load need this to actually execute
  if (state.analytics || state.marketing) {
    console.log('[Consent] Pushing page_view_after_consent event to dataLayer');
    window.dataLayer.push({
      'event': 'page_view_after_consent',
      'page_path': window.location.pathname,
      'page_title': document.title,
    });
  }
}

/**
 * Initialize consent from stored preferences
 * Call this after checking for existing consent
 */
export function initializeGTMConsent(state: ConsentState): void {
  ensureDataLayer();
  
  // If user has previously consented, update immediately
  if (state.analytics || state.marketing) {
    updateGTMConsent(state);
  }
}

/**
 * Check if GTM is loaded
 */
export function isGTMLoaded(): boolean {
  return window.dataLayer && window.dataLayer.some(
    (item) => typeof item === 'object' && item !== null && 'gtm.start' in (item as Record<string, unknown>)
  );
}

